import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/app/shells/AppShell';
import { FlowShell } from '@/app/shells/FlowShell';
import { cabinetFlowRoutes, cabinetRoutes } from '@/app/routes/cabinetRoutes';
import { adminRoutes } from '@/app/routes/adminRoutes';
import { PublicShell } from '@/app/shells/PublicShell';
import { publicRoutes } from '@/app/routes/publicRoutes';
import { AdminLoginPage } from '@/features/auth/AdminLoginPage';
import { AdminPasswordPage } from '@/features/auth/AdminPasswordPage';
import { MpassErrorPage } from '@/features/auth/MpassErrorPage';
import { MpassMockPage } from '@/features/auth/MpassMockPage';
import { common } from '@/i18n/dictionaries/common';
import { useTranslation } from '@/i18n/useTranslation';

// The catch-all. A component rather than a bare element, because the wording is translated and
// only a component can read the language.
function NotFound() {
  const { t } = useTranslation(common);
  return <div className="empty-state">{t('notFound')}</div>;
}

// Authenticated cabinet & administration SPA (served under /app/).
// Feature routes register in the route table of their zone (app/routes/) as their stories are
// implemented (US2+).
export const router = createBrowserRouter(
  [
    {
      element: <AppShell />,
      children: [...cabinetRoutes, ...adminRoutes, { path: '*', element: <NotFound /> }],
    },
    {
      // Single-flow dossier pages (a wizard, a read-only status view): no sidebar, own chrome.
      element: <FlowShell />,
      children: cabinetFlowRoutes,
    },
    // The MPass screens. Deliberately outside every shell: they are the sign-in boundary, not
    // pages of either zone, so they carry neither the cabinet sidebar nor the public site header.
    // Both must stay anonymous - wrapping either in RequireSession would send a signed-out
    // visitor to the login gate from the very screen that exists to stop that.
    //
    // They rank above AppShell's '*' catch-all, which would otherwise answer them with the
    // cabinet's own "not found" chrome.
    {
      // Reached by redirect from MPassSamlConfig's SAML failureHandler, MockAuthController's
      // blocked-account branch, and services/reauth.ts's loop guard.
      path: '/auth/mpass-error',
      element: <MpassErrorPage />,
    },
    {
      // Only resolves under the mock-gov profile: MockAuthController owns this path and is the
      // thing that forwards it here. In every other profile Spring Security's SAML entry point
      // answers first and the browser never reaches React.
      path: '/saml/authenticate/:registrationId',
      element: <MpassMockPage />,
    },
    // The administration's password sign-in and its password change (ADM-1), on the admin host:
    // sign-in screens like the two above, outside every shell and anonymous. The change page
    // needs a password session, which the server checks.
    { path: '/admin/autentificare', element: <AdminLoginPage /> },
    { path: '/admin/parola', element: <AdminPasswordPage /> },
    // The public zone — see publicRoutes for why it answers at the site root.
    {
      element: <PublicShell />,
      children: publicRoutes,
    },
  ],
  // No basename: the cabinet carries its own /app prefix on every route now, which frees the
  // site root for the public zone. A basename applies to every route in the router, so while it
  // was set there was no way for one React app to answer at both / and /app.
);
